export type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
export type InvoiceFrequency = 'monthly' | 'quarterly' | 'annual' | 'custom';

export interface Invoice {
  id: string;
  invoice_number: string;
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string;
  subtotal: number;
  tax_rate?: number;
  tax_amount: number;
  discount_rate?: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  notes?: string;
  payment_terms?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  
  // Relations
  organization?: any;
  project?: any;
  contract?: any;
  line_items?: InvoiceLineItem[];
  payments?: InvoicePayment[];
}

export interface InvoiceLineItem {
  id: string;
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  tax_rate?: number;
  project_milestone_id?: string;
  project_expense_id?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface InvoicePayment {
  id: string;
  invoice_id: string;
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
}

export interface InvoiceTemplateData {
  payment_terms?: string;
  tax_rate?: number;
  discount_rate?: number;
  currency?: string;
  notes?: string;
  line_items?: InvoiceLineItemFormData[];
}

export interface InvoiceTemplate {
  id: string;
  name: string;
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  frequency: InvoiceFrequency;
  next_invoice_date?: string;
  is_active: boolean;
  template_data: InvoiceTemplateData;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceSettings {
  id: string;
  organization_id: string;
  invoice_prefix: string;
  next_invoice_number: number;
  default_payment_terms?: string;
  default_notes?: string;
  logo_url?: string;
  company_details?: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    tax_id?: string;
    email?: string;
    phone?: string;
  };
  created_at: string;
  updated_at: string;
}

// Form types
export interface InvoiceFormData {
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  issue_date: string;
  due_date: string;
  tax_rate?: number;
  discount_rate?: number;
  currency: string;
  notes?: string;
  payment_terms?: string;
  line_items: InvoiceLineItemFormData[];
}

export interface InvoiceLineItemFormData {
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  project_milestone_id?: string;
  project_expense_id?: string;
}

export interface InvoicePaymentFormData {
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
}

// Calculation helpers
export interface InvoiceCalculation {
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
}

// Status helpers
export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled'
};